import { ReactNode } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileNav } from "@/components/MobileNav";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { NotificationPopup } from "@/components/NotificationPopup";
import { Chatbot } from "@/components/Chatbot";
import { Building2, Sparkles, Clock, LogOut, RefreshCcw, CreditCard, ShieldCheck, ExternalLink, UserRound } from "lucide-react";
import { authService, useDashboardSummary, useRenewDetails, useSignalR } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQueryClient } from "@tanstack/react-query";

interface LayoutProps {
  children: ReactNode;
}

// Get firm name synchronously to avoid flash of default text
const getCompanyName = (): string => {
  const user = authService.getCurrentUser();
  return user?.firmName || "PharmaSoft Director CS";
};

// Format date for display
const formatLastUpdatedDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
    // Convert lowercase am/pm to uppercase AM/PM
    return formatted.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
  } catch {
    return dateString;
  }
};

const formatRenewDate = (dateString: string): string => {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

const formatRenewDateShort = (dateString: string): string => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyName = getCompanyName();
  useSignalR();
  // Get firmId from authenticated user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;
  const firmIdNumber = firmId ? Number(firmId) : undefined;
  const { data: summaryData } = useDashboardSummary({ firmId: firmIdNumber });
  const { data: renewDetails } = useRenewDetails({ firmId: firmIdNumber });
  const lastUpdatedDate = summaryData?.lastUpdatedDate;

  const handleRefresh = () => {
    // Full page reload to get latest assets/data
    window.location.reload();
  };

  const handleLogout = async () => {
    try {
      // Clear all React Query cache first
      queryClient.clear();
      
      // Call auth service to clear tokens and stored data (including AI chat history)
      await authService.logout();
      
      // Clear all localStorage
      localStorage.clear();
      
      // Clear all sessionStorage
      sessionStorage.clear();
    } catch (error) {
      console.error("Error during logout:", error);
      // Even if there's an error, clear storage
      localStorage.clear();
      sessionStorage.clear();
    } finally {
      // Hard refresh by navigating to login with cache-busting parameter
      // This ensures all cached data is cleared and fresh data is loaded
      window.location.href = "/?logout=" + Date.now();
    }
  };

  const handleMyAccount = () => {
    navigate("/my-account");
  };

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="h-screen flex w-full bg-background overflow-hidden">
        <AppSidebar />
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <header className="sticky top-0 z-10 border-b bg-card">
            <div className="flex h-14 items-center gap-4 px-4 lg:px-6">
              <SidebarTrigger />
              <div className="flex items-center gap-3 flex-1">
                {/* Company Logo/Icon - Replace Building2 with <img> for custom logo */}
                <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg shadow-primary/25">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-lg lg:text-xl font-bold tracking-tight font-display bg-gradient-to-r from-primary via-primary/90 to-accent bg-clip-text text-transparent drop-shadow-sm">
                {companyName}
                </h1>
                <Sparkles className="w-4 h-4 text-amber-400 drop-shadow-sm" />
              </div>
              <div className="ml-auto flex items-center gap-3">
                {renewDetails?.isExpired === 1 && (
                  <div className="hidden md:flex items-center gap-2">
                    <span className="text-sm font-semibold text-destructive animate-pulse">
                      {formatRenewDateShort(renewDetails.renewDate)}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1 rounded-full bg-destructive text-white text-sm font-semibold shadow-sm hover:bg-destructive/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-destructive"
                          onClick={handleMyAccount}
                        >
                          Renew
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <span>Renewal required</span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
                {firmId && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {firmId}
                    </span>
                  </div>
                )}
                {lastUpdatedDate && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Last Updated: {formatLastUpdatedDate(lastUpdatedDate)}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleRefresh}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-muted-foreground/20 text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
                          aria-label="Refresh"
                        >
                          <RefreshCcw className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <span>Refresh</span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
                <NotificationPopup />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-md border border-muted-foreground/20 bg-background/60 text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-colors duration-200"
                      aria-label="Account menu"
                    >
                      <UserRound className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Account</DropdownMenuLabel>
                    <DropdownMenuItem onClick={handleMyAccount} className="flex items-center gap-2">
                      <UserRound className="w-4 h-4" />
                      <span>My Account</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="flex items-center gap-2 text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>
          <div className="flex-1 px-2 lg:px-3 pb-20 lg:pb-3 pt-1 lg:pt-2 min-h-0 overflow-y-auto">
            {children}
          </div>
          <footer className="border-t bg-card/50 backdrop-blur-sm py-3 px-4 lg:px-6">
            <p className="text-sm text-muted-foreground text-center">
              Powered By <span className="font-semibold text-primary">KireetiSoft</span>
            </p>
          </footer>
        </main>
        <MobileNav />
        <Chatbot />
      </div>
    </SidebarProvider>
  );
}
